import React, { useState, useEffect } from 'react'
import { Box, Flex, Progress, Text, CloseButton, VStack, Icon, Spinner, HStack } from '@chakra-ui/react'
import { toast } from 'react-toastify'
import { MdDownloading, MdCheckCircle, MdError } from 'react-icons/md'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { downloadJobs, DownloadJob } from '../../store/downloadJobs/slice'
import { useJobPoller } from '../../hooks/useJobPoller'
import { ExpensesService } from '../../services/Expenses'
import { ConciliationService } from '../../services/Conciliation'
import { useTranslation } from 'react-i18next'

const PHASE_LABELS: Record<string, string> = {
  extracting: 'download.phaseExtracting',
  writing: 'download.phaseWriting',
  uploading: 'download.uploadingFile',
  finalizing: 'download.preparingDownload'
}

const STATUS_CONFIG: Record<DownloadJob['status'], { label: string; color: string; icon: React.ElementType }> = {
  pending: { label: 'download.preparingYourFile', color: 'blue.500', icon: MdDownloading },
  processing: { label: 'download.generatingDownload', color: 'blue.500', icon: MdDownloading },
  uploading: { label: 'download.uploadingFile', color: 'blue.500', icon: MdDownloading },
  completed: { label: 'download.downloadCompleted', color: 'green.500', icon: MdCheckCircle },
  failed: { label: 'download.downloadFailed', color: 'red.500', icon: MdError }
}

const formatElapsed = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}

const ElapsedTime = ({ startedAt }: { startedAt: number }) => {
  const [elapsed, setElapsed] = useState(() => Date.now() - startedAt)

  useEffect(() => {
    const interval = setInterval(() => setElapsed(Date.now() - startedAt), 1000)
    return () => clearInterval(interval)
  }, [startedAt])

  return (
    <Text
      fontSize='xs'
      color='gray.400'
    >
      {formatElapsed(elapsed)}
    </Text>
  )
}

const DownloadJobItem = React.memo(({ job }: { job: DownloadJob }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const isActive = job.status === 'pending' || job.status === 'processing' || job.status === 'uploading'

  const statusLabel = t((isActive && job.currentPhase && PHASE_LABELS[job.currentPhase]) || STATUS_CONFIG[job.status].label)
  const config = STATUS_CONFIG[job.status]

  const getStatusFn = React.useCallback(
    (jobId: string) => {
      if (job.jobType === 'reconciliation') {
        return ConciliationService({ useLoader: false }).getDownloadStatus(jobId)
      }
      return ExpensesService({ useLoader: false }).getDownloadStatus(jobId)
    },
    [job.jobType]
  )

  useJobPoller(
    job.jobId,
    getStatusFn,
    () => toast.success(t('download.downloadCompleted'), { autoClose: 5000 }),
    () => toast.error(t('download.downloadError'), { autoClose: 8000 })
  )

  const getSubtext = () => {
    if (job.status === 'completed') return t('download.fileDownloaded')
    if (job.status === 'failed') return job.error || t('download.errorTryAgain')
    if (job.totalExpenses && job.processedExpenses !== null) {
      return t(job.jobType === 'reconciliation' ? 'download.processedTransactions' : 'download.processedExpenses', { processed: job.processedExpenses, total: job.totalExpenses })
    }
    return t('download.mayTakeMinutes')
  }

  return (
    <Box
      bg='white'
      borderRadius='lg'
      boxShadow='xl'
      p={4}
      width='320px'
      border='1px solid'
      borderColor='gray.200'
    >
      <Flex
        justify='space-between'
        align='center'
        mb={2}
      >
        <HStack
          spacing={2}
          flex={1}
        >
          {isActive ? (
            <Spinner
              size='sm'
              color={config.color}
              speed='0.8s'
            />
          ) : (
            <Icon
              as={config.icon}
              color={config.color}
              boxSize={5}
            />
          )}
          <Text
            fontSize='sm'
            fontWeight={600}
            color='gray.700'
            flex={1}
          >
            {statusLabel}
          </Text>
          {isActive && <ElapsedTime startedAt={job.startedAt} />}
        </HStack>
        <CloseButton
          size='sm'
          onClick={() => dispatch(downloadJobs.actions.removeJob(job.jobId))}
        />
      </Flex>
      <Progress
        value={job.progress}
        size='sm'
        colorScheme={job.status === 'failed' ? 'red' : job.status === 'completed' ? 'green' : 'blue'}
        borderRadius='full'
        hasStripe={isActive}
        isAnimated={isActive}
      />
      <Flex
        justify='space-between'
        align='center'
        mt={1}
      >
        <Text
          fontSize='xs'
          color='gray.400'
        >
          {getSubtext()}
        </Text>
        {isActive && (
          <Text
            fontSize='xs'
            color='gray.400'
            flexShrink={0}
            ml={2}
          >
            {job.progress}%
          </Text>
        )}
      </Flex>
    </Box>
  )
})

DownloadJobItem.displayName = 'DownloadJobItem'

export const DownloadProgressOverlay = React.memo(() => {
  const activeJobs = useAppSelector((state) => state.downloadJobs.activeJobs)

  if (activeJobs.length === 0) return null

  return (
    <Box
      position='fixed'
      bottom={20}
      right={4}
      zIndex={9999}
    >
      <VStack
        spacing={2}
        align='flex-end'
      >
        {activeJobs.map((job) => (
          <DownloadJobItem
            key={job.jobId}
            job={job}
          />
        ))}
      </VStack>
    </Box>
  )
})

DownloadProgressOverlay.displayName = 'DownloadProgressOverlay'
